import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Col, Table, Spinner, Badge, Row, Card, Button } from 'react-bootstrap';
import axios from 'axios';
import { toWords } from 'number-to-words';
import { BASE_URL } from '../../../services/Api';
import { authHeader } from '../../../services/auth-header';
import Swal from 'sweetalert2';

const ViewPumpDomesticSO = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [salesOrderData, setSalesOrderData] = useState(null);
  const [rows, setRows] = useState([]);

  const fetchSalesOrderData = async () => {
    try {
      const response = await axios.get(`${BASE_URL}salesOrder/findById/${id}`, {
        headers: authHeader()
      });

      const data = response.data;
      setSalesOrderData(data);

      if (data.salesOrderItems && data.salesOrderItems.length > 0) {
        setRows(data.salesOrderItems.map(item => ({
          description: item.product?.description || '-',
          productremarks: item.productremarks || "-",
          quantity: item.quantity || 0,
          unitrate: item.unitrate || 0,
          amount: item.amount || 0,
          hsnsacno: item.hsnsacno || "-",
          partno: item.partno || "-",
          serialnumber: item.serialnumber || "-",
          modelnumber: item.modelnumber || "-",
          tagnumber: item.tagnumber || "-",
          moc: item.moc || "-",
          itemcode: item.itemcode || "-",
          uom: item.uom || "-"
        })));
      }
    } catch (error) {
      console.error("Error fetching sales order data", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch sales order data. Please try again.",
      });
      navigate("/salesOrderIndexTable/1");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesOrderData();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const formatNumberWithCommas = (num) => {
    if (isNaN(num) || num === null || num === undefined) return '0.00';
    return parseFloat(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const numberToWordsWithRupees = (num) => {
    const amount = Math.round(num);
    const words = toWords(amount);

    // Capitalize first letter of each word
    const capitalizedWords = words.split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');

    return `${capitalizedWords} Rupees Only`;
  };

  if (isLoading) {
    return (
      <div className="container text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading sales order details...</p>
      </div>
    );
  }

  if (!salesOrderData) {
    return (
      <div className="container text-center py-5">
        <div className="alert alert-warning">No sales order data found</div>
      </div>
    );
  }

  // Calculate totals
  const nettotal = rows.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
  const pAndF = nettotal * (salesOrderData.packingandforwardingpercent / 100);
  const freight = nettotal * (salesOrderData.freightpercent / 100);
  const subtotal = nettotal + pAndF + freight;
  const gst = subtotal * (salesOrderData.gstpercent / 100);
  const grandtotalinr = subtotal + gst;

  return (
    <div className="container py-4">
      {/* Header Information Card */}
      <Card className="mb-4 shadow-sm">
        <Card.Header>
          <h5 className="mb-0">Sales Order Information</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>SO Number:</strong>
                <span>{salesOrderData.sono || '-'}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>SO Date:</strong>
                <span>{formatDate(salesOrderData.sodate)}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Quotation No:</strong>
                <span>{salesOrderData.quotationno || '-'}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Quotation Date:</strong>
                <span>{formatDate(salesOrderData.quotationdate)}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Customer PO No:</strong>
                <span>{salesOrderData.customerpono || '-'}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Customer PO Date:</strong>
                <span>{formatDate(salesOrderData.customerpodate)}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Customer Material Code:</strong>
                <span>{salesOrderData.customermaterialcode || '-'}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Delivery Term:</strong>
                <span>{salesOrderData.deliveryterm || '-'}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Payment Term:</strong>
                <span>{salesOrderData.paymentterm || '-'}</span>
              </div>
            </Col>
            {salesOrderData.ldclause && (
              <Col md={12} className="mb-3">
                <div>
                  <strong className="me-2">LD Clause:</strong>
                  <div className="ms-4 mt-1">{salesOrderData.ldclause}</div>
                </div>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {/* Customer Information Card */}
      <Card className="mb-4 shadow-sm">
        <Card.Header>
          <h5 className="mb-0">Customer Details</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Customer:</strong>
                <span>{salesOrderData.customer?.companyname || '-'}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Contact Person:</strong>
                <span>{salesOrderData.contactperson || '-'}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Contact No:</strong>
                <span>{salesOrderData.contactno || '-'}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Email:</strong>
                <span>{salesOrderData.email || '-'}</span>
              </div>
            </Col>
            <Col md={12}>
              <p>
                <strong>Customer Address:</strong>{" "}
                {salesOrderData.customeraddress?.customeraddress || "-"}
              </p>

              <p>
                {salesOrderData.customeraddress?.city?.cityname || "-"},{" "}
                {salesOrderData.customeraddress?.state?.statename || "-"},{" "}
                {salesOrderData.customeraddress?.pincode || "-"}
              </p>

              <p>
                {salesOrderData.customeraddress?.country?.countryname || "-"}
              </p>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Branch & Warehouse Details */}
      <Card className="mb-4 shadow-sm">
        <Card.Header>
          <h5 className="mb-0">Branch & Warehouse Details</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Branch:</strong>
                <span>{salesOrderData.branch?.branchname || '-'}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Warehouse:</strong>
                <span>{salesOrderData.warehouse?.warehousename || '-'}</span>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Sales Order Items Card */}
      <Card className="mb-4 shadow-sm">
        <Card.Header>
          <h5 className="mb-0">Sales Order Items</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table striped bordered hover className="mb-0">
              <thead className="table-dark">
                <tr>
                  <th width="40">#</th>
                  <th>Product Description</th>
                  <th>Part No</th>
                  <th>Serial No</th>
                  <th>Model No</th>
                  <th>Tag No</th>
                  <th>MOC</th>
                  <th>HSN Code</th>
                  <th>Item Code</th>
                  <th>Quantity</th>
                  <th>UOM</th>
                  <th>Unit Rate (₹)</th>
                  <th>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      {row.description}
                      {row.productremarks && row.productremarks !== '-' && (
                        <div className="text-muted small mt-2">
                          <strong>Remarks:</strong> {row.productremarks}
                        </div>
                      )}
                    </td>
                    <td>{row.partno}</td>
                    <td>{row.serialnumber}</td>
                    <td>{row.modelnumber}</td>
                    <td>{row.tagnumber}</td>
                    <td>{row.moc}</td>
                    <td>{row.hsnsacno}</td>
                    <td>{row.itemcode}</td>
                    <td className="text-end">{row.quantity || '0'}</td>
                    <td>{row.uom}</td>
                    <td className="text-end">{formatNumberWithCommas(row.unitrate)}</td>
                    <td className="text-end">{formatNumberWithCommas(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Calculation Summary Card */}
      {/* <Card className="mb-4 shadow-sm">
        <Card.Header>
          <h5 className="mb-0">Amount Calculation</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table striped bordered hover className="mb-0">
              <thead>
                <tr>
                  <th width="50%">Description</th>
                  <th width="20%">Percentage</th>
                  <th width="30%">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Net Total</td>
                  <td>-</td>
                  <td className="text-start">{formatNumberWithCommas(nettotal)}</td>
                </tr>
                <tr>
                  <td>Packing & Forwarding</td>
                  <td className="text-start">{salesOrderData.packingandforwardingpercent || '0'}%</td>
                  <td className="text-start">{formatNumberWithCommas(pAndF)}</td>
                </tr>
                <tr>
                  <td>Freight</td>
                  <td className="text-start">{salesOrderData.freightpercent || '0'}%</td>
                  <td className="text-start">{formatNumberWithCommas(freight)}</td>
                </tr>
                <tr>
                  <td>Subtotal</td>
                  <td>-</td>
                  <td className="text-start">{formatNumberWithCommas(subtotal)}</td>
                </tr>
                <tr>
                  <td>GST</td>
                  <td className="text-start">{salesOrderData.gstpercent || '18'}%</td>
                  <td className="text-start">{formatNumberWithCommas(gst)}</td>
                </tr>
                <tr className="table-success">
                  <td><strong>Grand Total</strong></td>
                  <td>-</td>
                  <td className="text-start"><strong>{formatNumberWithCommas(grandtotalinr)}</strong></td>
                </tr>
                <tr>
                  <td colSpan="3">
                    <div className="p-2">
                      <strong>Grand Total in Words:</strong>
                      <div className="ms-2 mt-1 fst-italic">{numberToWordsWithRupees(grandtotalinr)}</div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card> */}

      <div className="card mb-4 shadow-sm">
        <div className="card-header">
          <h5 className="mb-0">Amount Calculation</h5>
        </div>
        <div className="card-body p-0">
          <div className="list-group list-group-flush">
            <div className="list-group-item">
              <div className="row">
                <div className="col-6">Net Total</div>
                <div className="col-2">-</div>
                <div className="col-4 text-end">{formatNumberWithCommas(nettotal)}</div>
              </div>
            </div>

            <div className="list-group-item">
              <div className="row">
                <div className="col-6">Packing & Forwarding</div>
                <div className="col-2">{salesOrderData.packingandforwardingpercent || '0'}%</div>
                <div className="col-4 text-end">{formatNumberWithCommas(pAndF)}</div>
              </div>
            </div>

            <div className="list-group-item">
              <div className="row">
                <div className="col-6">Freight</div>
                <div className="col-2">{salesOrderData.freightpercent || '0'}%</div>
                <div className="col-4 text-end">{formatNumberWithCommas(freight)}</div>
              </div>
            </div>

            <div className="list-group-item">
              <div className="row">
                <div className="col-6">Subtotal</div>
                <div className="col-2">-</div>
                <div className="col-4 text-end">{formatNumberWithCommas(subtotal)}</div>
              </div>
            </div>

            <div className="list-group-item">
              <div className="row">
                <div className="col-6">GST</div>
                <div className="col-2">{salesOrderData.gstpercent || '18'}%</div>
                <div className="col-4 text-end">{formatNumberWithCommas(gst)}</div>
              </div>
            </div>

            <div className="list-group-item bg-light">
              <div className="row">
                <div className="col-6"><strong>Grand Total</strong></div>
                <div className="col-2">-</div>
                <div className="col-4 text-end"><strong>{formatNumberWithCommas(grandtotalinr)}</strong></div>
              </div>
            </div>

            <div className="list-group-item">
              <div className="row">
                <div className="col-12">
                  <div className="p-2">
                    <strong>Grand Total in Words:</strong>
                    <div className="ms-2 mt-1 fst-italic">{numberToWordsWithRupees(grandtotalinr)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end mb-4">
        <Button
          variant="outline-secondary"
          onClick={() => navigate(-1)}
          className="me-2"
        >
          <i className="bi bi-arrow-left me-2"></i>BACK
        </Button>
      </div>
    </div>
  );
};

export default ViewPumpDomesticSO;
